<template>
  <CollapsiblePanel
    :variant="['default', 'rounded', '30-12-paddings']"
    :label="
      $t('_web_tournament_settings_registration-form', 'Registration form')
    "
    class="registration-form-header"
  >
    <div class="registration-custom-wrapper">
      <div class="row  registration-form-include">
        <HeraFormSwitch
          v-model="requiresRegistrationForm"
          class="col-4"
          name="substitutesSwitch"
          :label="
            $t(
              '_web_tournament_reg-form_registration-form-label-test',
              'Include registration form?'
            )
          "
          :disabled="registrationFormDisabled"
        />
      </div>

      <div
        class="row registration-form-description"
        :class="{
          disabled: !requiresRegistrationForm || registrationFormDisabled,
        }"
      >
        <div class="col-8">
          <TextInputMultiline
            v-model="infoTitleModel"
            name="infoTitle"
            :read-only="!hasForm"
            :error="errors.first('infoTitle')"
            :rows="3"
            :placeholder="$$t('Description', 'info-placeholder')"
            :maxlength="INTRO_TEXT_MAX_LENGTH"
            has-counter
            @blur="() => (infoTitle = infoTitleModel)"
          />
        </div>
      </div>

      <!-- <Tabs
      class="mb-5"
      :class="{ disabled: !hasForm }"
      v-bind="{ tabs, activeTab }"
      use-custom
      @change="toggleTab"
    /> -->
      <div class="row registration-help-text">
        <div class="col">
          <TextBlock variant="secondary"> {{ fieldsHelpText }}</TextBlock>
        </div>
      </div>
      <div class="row registration-span-title">
        <div class="col-4">
          <TextLabel :text="$$t('Title:', 'header-fields-title')" required />
        </div>
        <div class="col-2">
          <TextLabel :text="$$t('Type:', 'header-fields-type')" required />
        </div>
        <div class="col-2">
          <TextLabel :text="$$t('Required:', 'header-fields-required')" />
        </div>
      </div>
      <draggable v-model="customFields" handle=".move-column">
        <div
          v-for="(field, index) in customFields"
          :key="`personal-${field.field_type}-${index}`"
          class="builder-row row mb-5 registration-fields"
        >
          <div class="col-auto position-relative">
            <div
              class="position-absolute d-flex align-items-center move-icon-wrapper"
              :class="{ 'move-column': hasForm }"
            >
              <Icon name="action" width="16" :fill="$scss.secondaryTextColor" />
            </div>
          </div>
          <div class="col-4">
            <TextInput
              v-validate.disable="'required'"
              :value="
                field.touched
                  ? field.label
                  : $$t(
                      field.label,
                      isPersonal
                        ? `fields-associate-${field.associate_field}`
                        : `clan-fields-associate-${field.associate_field}`,
                      {},
                      '-'
                    )
              "
              :data-vv-name="`fieldLabel-${index}`"
              :error="errors.first(`fieldLabel-${index}`)"
              :class="{ 'is-invalid': errors.first(`fieldLabel-${index}`) }"
              type="text"
              :read-only="!hasForm"
              :disabled="!hasForm"
              :placeholder="$$t('Type a value', 'fields-label-placeholder')"
              :data-vv-as="$$t('Title:', 'header-fields-title')"
              @change="value => setFieldLabel(index, value)"
            />
          </div>
          <div class="col-2">
            <HeraFormDropdown
              v-validate.disable="'required'"
              :value="field.field_type"
              :disabled="!!field.associate_field || !hasForm"
              :name="`fieldType-${index}`"
              :data-vv-name="`fieldType-${index}`"
              :data-vv-as="$$t('Type:', 'header-fields-type')"
              :list="Object.keys(fieldTypes)"
              :class="{ 'is-invalid': errors.first(`fieldType-${index}`) }"
              variant="input-like"
              class="w-100"
              :error="errors.first(`fieldType-${index}`)"
              :empty-text="$$t('Choose option', 'field-type_empty')"
              border-bot
              theme-dark
              empty-option
              @input="value => setFieldType(index, value)"
            >
              <template slot="chosen" slot-scope="chosen">
                {{
                  $$t(
                    fieldTypes[chosen.chosenItem],
                    `fields-type-${field.field_type}`
                  )
                }}
              </template>

              <template slot="item" slot-scope="{ item }">
                {{ $$t(fieldTypes[item], `fields-type-${item}`) }}
              </template>
            </HeraFormDropdown>
          </div>
          <div class="col-auto">
            <HeraFormSwitch
              :value="field.required"
              :disabled="!hasForm"
              :read-only="!hasForm"
              @input="value => setFieldRequired(index, value)"
            />
          </div>
          <div class="col d-flex justify-content-start align-items-center">
            <Icon
              class="d-block cursor-pointer"
              :class="{ disabled: !hasForm }"
              name="delete"
              width="16"
              :fill="$scss.primaryTextBg"
              @click="deleteRow(index)"
            />
          </div>
        </div>
      </draggable>
      <div class="row">
        <div class="col">
          <HeraFormBtn
            class="btn-secondary-simple"
            :disabled="!hasForm"
            @click.native="addRow"
          >
            <Icon name="add" width="20" class="h-mr-3" />
            <TextBlock :text="$$t('Custom field', 'add-field-btn')" />
          </HeraFormBtn>
        </div>
      </div>
    </div>
  </CollapsiblePanel>
</template>

<script>
import { cloneDeep, isEmpty, get } from '@/utils/lodashUtils';
import { mapGetters, mapMutations } from 'vuex';
import { translate } from '@/mixins/translate';
import draggable from 'vuedraggable';

import CollapsiblePanel from '@/components/atoms/CollapsiblePanel';
import TextBlock from '@/components/atoms/TextBlock';
import TextInputMultiline from '@/components/molecules/form/TextInputMultiline';
import HeraFormSwitch from '@/components/atoms/form/HeraFormSwitch';
import HeraFormBtn from '@/components/atoms/HeraFormBtn';
import HeraFormDropdown from '@/components/molecules/form/HeraFormDropdown';
import TextInput from '@/components/atoms/form/HeraFormTextInput';
import TextLabel from '@/components/atoms/TextLabel';
import Icon from '@/components/atoms/Icon';
// import Tabs from '@/components/common/HorizontalTabs';

const DEFAULT_CUSTOM_FIELDS = () => ({
  index: '',
  field_type: '',
  label: '',
  placeholder: '',
  associate_field: '',
  required: false,
});
const predefinedFields = () => [
  {
    index: 0,
    field_type: 'input',
    label: 'First Name',
    placeholder: 'Your first name',
    associate_field: 'first_name',
    required: false,
  },
  {
    index: 2,
    field_type: 'input',
    label: 'Last Name',
    placeholder: 'Your last name',
    associate_field: 'last_name',
    required: false,
  },
  {
    index: 4,
    field_type: 'date',
    label: 'Date of Birth',
    placeholder: 'DD MM YYYY',
    associate_field: 'date_of_birth',
    required: false,
  },
  {
    index: 5,
    field_type: 'country',
    label: 'Country',
    placeholder: 'Select your country',
    associate_field: 'country',
    required: false,
  },
];
const predefinedFieldsClans = () => [
  {
    index: 0,
    field_type: 'input',
    label: 'Name',
    placeholder: 'Clan name',
    associate_field: 'clan_name',
    required: false,
  },
  {
    index: 1,
    field_type: 'country',
    label: 'Country',
    placeholder: 'Select your country',
    associate_field: 'country',
    required: false,
  },
  {
    index: 2,
    field_type: 'city',
    label: 'City',
    placeholder: 'Select your city',
    associate_field: 'city',
    required: false,
  },
];
const INTRO_TEXT_MAX_LENGTH = 500;
const component = 'registration_form';

export default {
  name: 'RegistrationForm',
  inject: ['$validator'],
  components: {
    TextBlock,
    TextInputMultiline,
    HeraFormSwitch,
    HeraFormBtn,
    HeraFormDropdown,
    Icon,
    draggable,
    TextInput,
    TextLabel,
    CollapsiblePanel,
    // Tabs,
  },
  mixins: [translate('_web_tournament_reg-form')],

  data() {
    return {
      activeTab: 'personal',
      infoTitleModel: '',
      fieldTypes: {
        input: 'Input',
        country: 'Country',
        city: 'City',
        number: 'Number',
        // time: 'Time',
        date: 'Date',
      },
      predefinedFields: predefinedFields(),
      INTRO_TEXT_MAX_LENGTH,
    };
  },
  computed: {
    ...mapGetters([
      'currentTournament',
      'isSingle',
      'isTournamentFieldDisabled',
    ]),
    tabs() {
      return [
        {
          key: 'personal',
          label: this.$$t('Participant fields', 'personal-tab'),
        },
        {
          key: 'clans',
          label: this.$$t('Clan fields', 'clan-tab'),
          disabled:
            this.$lodash.get(
              this.currentTournament,
              'players_config.squad_size',
              0
            ) < 2,
        },
      ];
    },
    fieldsHelpText() {
      return this.isPersonal
        ? this.$$t(
            'Request a personal info about participant (for team -  must be filled out for each team member separately)',
            'participant-fields-tooltip'
          )
        : this.$$t(
            'Request info related with a team itself',
            'clan-fields-tooltip'
          );
    },
    isPersonal() {
      return this.activeTab === 'personal';
    },
    componentData() {
      return this.currentTournament[component];
    },
    currentFieldName() {
      return this.isPersonal ? 'custom_fields' : 'custom_clan_fields';
    },
    registrationFormDisabled() {
      return this.isTournamentFieldDisabled('registration_form');
    },
    requiresRegistrationForm: {
      get() {
        return get(this.componentData, 'requires_registration_form', false);
      },
      set(newVal) {
        this.updateCurrentTournament({
          requires_registration_form: newVal,
          component,
        });

        if (!newVal && !this.$route.params.tournamentID) {
          this.deleteCurrentTournamentProp('custom_fields');
          this.deleteCurrentTournamentProp('custom_clan_fields');
        } else {
          if (this.isPersonal) {
            this.setPredefinedFields(true);
          }
        }
      },
    },
    isSubstitutesAvailable() {
      return this.squadSize > 1;
    },
    hasForm() {
      return !!this.requiresRegistrationForm && !this.registrationFormDisabled;
    },
    infoTitle: {
      get() {
        return !isEmpty(this.componentData)
          ? this.componentData.info_title
          : '';
      },
      set(newVal) {
        this.updateCurrentTournament({
          info_title: newVal,
          component,
        });
      },
    },
    customFields: {
      get() {
        const fields = get(this.componentData, this.currentFieldName, []);
        return this.$lodash.isEmpty(fields)
          ? this.getPredefinedFields()
          : fields;
      },
      set(newVal) {
        let fields = cloneDeep(newVal);
        fields = fields.map((field, index) => ({
          ...field,
          index,
        }));
        this.updateCurrentTournament({
          [this.currentFieldName]: fields,
          component,
        });
      },
    },
  },
  watch: {
    'currentTournament.players_config.squad_size'(newVal) {
      this.toggleTab(newVal > 1 ? 'clans' : 'personal');
    },
  },
  mounted() {
    this.setPredefinedFields();
    this.toggleTab(this.isSingle ? 'personal' : 'clans');
  },
  methods: {
    ...mapMutations(['updateCurrentTournament', 'deleteCurrentTournamentProp']),
    setPredefinedFields(forceUpdate = false) {
      const noFields = this.$lodash.isEmpty(this.customFields);
      const fieldsAreDefault = this.$lodash.isEqual(
        this.customFields,
        this.getPredefinedFields()
      );

      if (forceUpdate || noFields || fieldsAreDefault) {
        this.customFields = this.getPredefinedFields();
      }
    },
    getPredefinedFields() {
      return this.activeTab === 'clans'
        ? predefinedFieldsClans()
        : predefinedFields();
    },
    addRow() {
      let customFields = cloneDeep(this.customFields);

      const newField = DEFAULT_CUSTOM_FIELDS();

      if (!isEmpty(customFields)) {
        customFields.push(newField);
      } else {
        customFields = [newField];
      }

      this.updateCurrentTournament({
        [this.currentFieldName]: customFields,
        component,
      });
    },
    deleteRow(index) {
      if (!this.requiresRegistrationForm) {
        return;
      }
      let customFields = cloneDeep(this.customFields);
      if (
        customFields[index].associate_field &&
        this.predefinedFields.findIndex(
          el => customFields[index].associate_field === el.associate_field
        ) < 0
      ) {
        this.predefinedFields = [...this.predefinedFields, customFields[index]];
      }

      customFields.splice(index, 1);

      this.updateCurrentTournament({
        [this.currentFieldName]: customFields,
        component,
      });
    },
    addField(index) {
      if (!this.hasForm) {
        return;
      }
      this.customFields = [...this.customFields, this.predefinedFields[index]];
      this.predefinedFields.splice(index, 1);
    },
    setFieldRequired(index, value) {
      this.updateCurrentData(index, 'required', value);
    },
    setFieldType(index, value) {
      this.updateCurrentData(index, 'field_type', value);
    },
    setFieldLabel(index, value) {
      this.updateCurrentData(index, 'label', value);
    },
    updateCurrentData(index, name, value) {
      const fields = cloneDeep(this.customFields);

      if (fields[index][name] !== value) {
        fields[index].touched = true;
      }

      fields[index][name] = value;

      this.updateCurrentTournament({
        [this.currentFieldName]: fields,
        component,
      });
    },
    toggleTab(key) {
      // if (!this.hasForm) {
      //   return;
      // }
      this.activeTab = key;

      this.deleteCurrentTournamentProp({
        component,
        prop:
          this.activeTab === 'clans' ? `custom_fields` : `custom_clan_fields`,
      });
      this.setPredefinedFields();
    },
  },
};
</script>
<style lang="scss">
.registration-custom-wrapper {
  padding: 13px !important;
  .form-radio--flat:not(.checked) > label {
    background-color: $ph-input-bg;
  }
  .move-column {
    cursor: grab;
  }

  .move-icon-wrapper {
    top: 0;
    bottom: 0;
  }

  .sortable-ghost {
    background-color: #282834;
  }

  .badge {
    color: $ph-card;
    font-weight: normal;
    background-color: $ph-green;
  }
  .dropdown_activator {
    width: 100%;
  }
}

.registration-form-header .hera-subheader--normal span {
  font-size: $hr-heading-5 !important;
}

.registration-form-include label {
  padding-bottom: 15px;
  font-size: $hr-text-small !important;
  font-weight: 600;
}

.registration-form-include .segment-control-btn {
  background-color: $hr-bg-header;
}

.registration-form-description textarea {
  background-color: $hr-bg-header !important;
  margin: 15px 0px 15px 0px;
  border: 2px solid transparent;
}

.registration-form-description textarea :focus {
  border: 2px solid $hr-bg-accent;
}

.registration-help-text {
  font-size: $hr-text-small !important;
  font-weight: 600;
  padding-top: 10px;
  margin-bottom: 25px;
}

.registration-span-title label {
  font-size: $hr-text-small !important;
  font-weight: 600;
  padding-bottom: 10px;
  padding-left: 30px;
}

.registration-fields input {
  background-color: $hr-bg-header;
  border: 2px solid transparent;
}

.registration-fields input :focus {
  border: 2px solid $hr-bg-accent;
}

.registration-fields button {
  background-color: $hr-bg-header;
}

.registration-fields .segment-control-btn {
  background-color: $hr-bg-header;
}
</style>
